// Level management module for PPT Hero game
export default class LevelManager {
  constructor(scene) {
    this.scene = scene;
  }

  // Get levels data
  getLevelsData() {
    return [
      // Level 0: Practice
      {
        id: 0,
        name: "Practice",
        instructions: "Practice mode: Drag shapes around to get familiar with the controls.",
        shapes: [
          { x: 100, y: 100, w: 80, h: 60, color: 0xff6b6b },
          { x: 200, y: 150, w: 60, h: 80, color: 0x4ecdc4 },
          { x: 300, y: 120, w: 70, h: 70, color: 0x45b7d1 }
        ],
        goal: (scene) => {
          // Practice mode: No automatic goal checking - only manual success via shortcuts
          return false; // Never auto-complete, only via shortcut success
        },
        timeLimit: 0 // No time limit for practice
      },
      // LEVEL 1
      {
        id: 1,
        name: "Basic Alignment",
        instructions: "1. Select all shapes (Ctrl+A)\n2. Use Alt,H,G,A,R to align them to the RIGHT edge!",
        shapes: [
          { x: 300, y: 200, w: 80, h: 100, color: 0x1ea7e1 },
          { x: 400, y: 300, w: 120, h: 80, color: 0x176b82 },
          { x: 250, y: 250, w: 90, h: 110, color: 0x6db832 },
        ],
        goal: (scene) => {
          console.log('🚨 GOAL FUNCTION CALLED - LEVEL 1 DEBUG TEST');
          // Debug: Check if shapes exist
          if (!scene.allShapes || scene.allShapes.length === 0) {
            console.log('❌ No shapes found!');
            return false;
          }
          
          console.log('🔍 LEVEL 1 GOAL DEBUG:');
          console.log('- Shapes count:', scene.allShapes.length);
          
          // Check if all shapes are within 5px of the rightmost edge
          let maxRight = -Infinity;
          scene.allShapes.forEach((s, index) => {
            const right = s.x + s.displayWidth / 2;
            console.log(`- Shape ${index}: x=${s.x}, width=${s.displayWidth}, rightEdge=${right}`);
            if (right > maxRight) maxRight = right;
          });
          
          console.log('- Max right edge:', maxRight);
          
          // Check alignment of each shape
          let alignedCount = 0;
          for (let i = 0; i < scene.allShapes.length; i++) {
            const s = scene.allShapes[i];
            const right = s.x + s.displayWidth / 2;
            const distance = Math.abs(right - maxRight);
            console.log(`- Shape ${i} alignment: rightEdge=${right}, distance=${distance}, aligned=${distance <= 5}`);
            if (distance <= 5) {
              alignedCount++;
            }
          }
          
          const allAligned = alignedCount === scene.allShapes.length;
          console.log('- Alignment result:', `${alignedCount}/${scene.allShapes.length} shapes aligned`, allAligned);
          
          // Check shortcut usage
          console.log('- Analytics data:', scene.analyticsData);
          console.log('- Analytics data type:', typeof scene.analyticsData);
          console.log('- Shortcuts used object:', scene.analyticsData?.shortcutsUsed);
          console.log('- Shortcuts used type:', typeof scene.analyticsData?.shortcutsUsed);
          
          const shortcutsUsed = scene.analyticsData?.shortcutsUsed;
          const shortcutCount = shortcutsUsed?.["alt,h,g,a,r"] || 0;
          const shortcutUsed = shortcutCount > 0;
          console.log('- Right align shortcut used:', shortcutUsed, 'count:', shortcutCount);
          
          const result = allAligned && shortcutUsed;
          console.log('🎯 FINAL RESULT:', result, '(aligned:', allAligned, '+ shortcut:', shortcutUsed, ')');
          
          return result;
        },
        timeLimit: 60
      },
      {
        // LEVEL 2
        id: 2,
        name: "Align Left",
        instructions: "1. Select all shapes (Ctrl+A)\n2. Use Alt,H,G,A,L to align them to the LEFT edge!",
        shapes: [
          { x: 500, y: 200, w: 60, h: 100, color: 0x1ea7e1 },
          { x: 600, y: 300, w: 120, h: 80, color: 0x176b82 },
          { x: 550, y: 400, w: 90, h: 110, color: 0x6db832 },
          { x: 480, y: 350, w: 70, h: 70, color: 0xff6600 },
        ],
        goal: (scene) => {
          // Find the leftmost edge
          let minLeft = Infinity;
          scene.allShapes.forEach((s) => {
            const left = s.x - s.displayWidth / 2;
            if (left < minLeft) minLeft = left;
          });
          
          // All shapes must be aligned to that left edge
          for (let s of scene.allShapes) {
            const left = s.x - s.displayWidth / 2;
            if (Math.abs(left - minLeft) > 5) return false;
          }
          
          // Verify the shortcut was used
          return scene.analyticsData && scene.analyticsData.shortcutsUsed && scene.analyticsData.shortcutsUsed["alt,h,g,a,l"] > 0;
        },
        timeLimit: 60
      },
      // Level 3: Align to top edge
      {
        id: 3,
        name: "Align Top",
        instructions: "1. Select all shapes (Ctrl+A)\n2. Use Alt,H,G,A,T to align them to the TOP edge!",
        shapes: [
          { x: 250, y: 180, w: 100, h: 90, color: 0xff6600 },
          { x: 350, y: 220, w: 80, h: 120, color: 0xff9900 },
          { x: 450, y: 150, w: 110, h: 80, color: 0xffcc00 },
          { x: 550, y: 200, w: 70, h: 100, color: 0xffff00 },
          { x: 650, y: 170, w: 90, h: 70, color: 0x33cc33 },
        ],
        goal: (scene) => {
          // Calculate the topmost edge
          let minTop = Infinity;
          scene.allShapes.forEach((s) => {
            const top = s.y - s.displayHeight / 2;
            if (top < minTop) minTop = top;
          });
          
          // All shapes must be aligned to that top edge
          for (let s of scene.allShapes) {
            const top = s.y - s.displayHeight / 2;
            if (Math.abs(top - minTop) > 5) return false;
          }
          
          // Verify the shortcut was used
          return scene.analyticsData.shortcutsUsed["alt,h,g,a,t"] > 0;
        },
        timeLimit: 60
      },
      
      // Level 4: Align to bottom edge
      {
        id: 4,
        name: "Align Bottom",
        instructions: "1. Select all shapes (Ctrl+A)\n2. Use Alt,H,G,A,B to align them to the BOTTOM edge!",
        shapes: [
          { x: 300, y: 350, w: 90, h: 110, color: 0x00ccff },
          { x: 400, y: 380, w: 120, h: 80, color: 0x0099cc },
          { x: 500, y: 360, w: 100, h: 90, color: 0x006699 },
          { x: 600, y: 400, w: 80, h: 120, color: 0x003366 },
          { x: 700, y: 330, w: 70, h: 70, color: 0x663399 },
        ],
        goal: (scene) => {
          // Find the bottom-most edge
          let maxBottom = -Infinity;
          scene.allShapes.forEach((s) => {
            const bottom = s.y + s.displayHeight / 2;
            if (bottom > maxBottom) maxBottom = bottom;
          });
          
          // All shapes must be aligned to that bottom edge
          for (let s of scene.allShapes) {
            const bottom = s.y + s.displayHeight / 2;
            if (Math.abs(bottom - maxBottom) > 5) return false;
          }
          
          // Verify the shortcut was used
          return scene.analyticsData.shortcutsUsed["alt,h,g,a,b"] > 0;
        },
        timeLimit: 60
      },
      
      // Level 5: Horizontal distribution
      {
        id: 5,
        name: "Horizontal Distribution",
        instructions: "1. Select all shapes (Ctrl+A)\n2. Use Alt,H,G,A,H to evenly distribute them horizontally!\n(Leftmost & rightmost remain fixed.)",
        shapes: [
          { x: 150, y: 300, w: 80, h: 80, color: 0xff3366 },
          { x: 250, y: 300, w: 80, h: 80, color: 0xff6699 },
          { x: 320, y: 300, w: 80, h: 80, color: 0xff99cc },
          { x: 450, y: 300, w: 80, h: 80, color: 0xffccff },
          { x: 500, y: 300, w: 80, h: 80, color: 0xff9900 },
          { x: 600, y: 300, w: 80, h: 80, color: 0xffcc00 },
        ],
        goal: (scene) => {
          // Sort shapes by x position
          const sorted = [...scene.allShapes].sort((a, b) => a.x - b.x);
          const leftX = sorted[0].x;
          const rightX = sorted[sorted.length - 1].x;
          
          // Check if middle shapes are evenly distributed
          const expectedGap = (rightX - leftX) / (sorted.length - 1);
          
          for (let i = 1; i < sorted.length - 1; i++) {
            const expectedX = leftX + expectedGap * i;
            if (Math.abs(sorted[i].x - expectedX) > 5) return false;
          }
          
          // Verify the shortcut was used
          return scene.analyticsData.shortcutsUsed["alt,h,g,a,h"] > 0;
        },
        timeLimit: 60
      },
      // Level 6: Vertical distribution
      {
        id: 6,
        name: "Vertical Distribution",
        instructions: "1. Select all shapes (Ctrl+A)\n2. Use Alt,H,G,A,V to evenly distribute them vertically!\n(Topmost & bottommost remain fixed.)",
        shapes: [
          { x: 400, y: 100, w: 90, h: 90, color: 0x33cc33 },
          { x: 400, y: 180, w: 90, h: 90, color: 0x66cc66 },
          { x: 400, y: 250, w: 90, h: 90, color: 0x99cc99 },
          { x: 400, y: 340, w: 90, h: 90, color: 0xccffcc },
          { x: 400, y: 420, w: 90, h: 90, color: 0x33cc33 },
          { x: 400, y: 550, w: 90, h: 90, color: 0x66cc66 },
        ],
        goal: (scene) => {
          // Sort shapes by y position
          const sorted = [...scene.allShapes].sort((a, b) => a.y - b.y);
          const topY = sorted[0].y;
          const bottomY = sorted[sorted.length - 1].y;
          
          // Check if middle shapes are evenly distributed
          const expectedGap = (bottomY - topY) / (sorted.length - 1);
          
          for (let i = 1; i < sorted.length - 1; i++) {
            const expectedY = topY + expectedGap * i;
            if (Math.abs(sorted[i].y - expectedY) > 5) return false;
          }
          
          // Verify the shortcut was used
          return scene.analyticsData.shortcutsUsed["alt,h,g,a,v"] > 0;
        },
        timeLimit: 60
      },
      // Level 7: Center Alignment
      // Level 7: Center Alignment (Horizontal)
      {
        id: 7,
        name: "Center Alignment",
        instructions: "1. Select all shapes (Ctrl+A)\n2. Use Alt,H,G,A,C to align them to the CENTER (horizontally)!",
        shapes: [
          { x: 200, y: 200, w: 80, h: 100, color: 0x663399 },
          { x: 300, y: 300, w: 120, h: 80, color: 0x9966cc },
          { x: 400, y: 400, w: 90, h: 110, color: 0xcc99ff },
          { x: 500, y: 500, w: 70, h: 70, color: 0xff6600 },
        ],
        goal: (scene) => {
          // Check if the shortcut was used
          if (!(scene.analyticsData && 
                scene.analyticsData.shortcutsUsed && 
                scene.analyticsData.shortcutsUsed["alt,h,g,a,c"] > 0)) {
            return false;
          }
          
          // Check if all shapes share approximately the same x-coordinate (horizontal alignment)
          if (scene.allShapes.length <= 1) return true;
          
          // Get the x-coordinate of the first shape as reference
          const referenceX = scene.allShapes[0].x;
          const tolerance = 15; // Reasonable tolerance for alignment
          
          // Check if all other shapes have similar x-coordinates
          for (let i = 1; i < scene.allShapes.length; i++) {
            if (Math.abs(scene.allShapes[i].x - referenceX) > tolerance) {
              return false;
            }
          }
          
          return true;
        },
        timeLimit: 60
      },

      // Level 8: Vertical Center Alignment (Middle)
      {
        id: 8,
        name: "Vertical Center Alignment",
        instructions: "1. Select all shapes (Ctrl+A)\n2. Use Alt,H,G,A,M to align them to the MIDDLE (vertically)!",
        shapes: [
          { x: 200, y: 150, w: 80, h: 80, color: 0xff3366 },
          { x: 350, y: 200, w: 80, h: 80, color: 0xff6699 },
          { x: 500, y: 250, w: 80, h: 80, color: 0x66cc66 },
          { x: 650, y: 300, w: 80, h: 80, color: 0x33cc33 },
        ],
        goal: (scene) => {
          // Check if the shortcut was used
          if (!(scene.analyticsData && 
                scene.analyticsData.shortcutsUsed && 
                scene.analyticsData.shortcutsUsed["alt,h,g,a,m"] > 0)) {
            return false;
          }
          
          // Check if all shapes share approximately the same y-coordinate (vertical alignment)
          if (scene.allShapes.length <= 1) return true;
          
          // Get the y-coordinate of the first shape as reference
          const referenceY = scene.allShapes[0].y;
          const tolerance = 15; // Reasonable tolerance for alignment
          
          // Check if all other shapes have similar y-coordinates
          for (let i = 1; i < scene.allShapes.length; i++) {
            if (Math.abs(scene.allShapes[i].y - referenceY) > tolerance) {
              return false;
            }
          }
          
          return true;
        },
        timeLimit: 60
      },
      
      // LEVEL 9
      {
        id: 9,
        name: "Complex Organization",
        instructions: "Level 9: Organize these shapes:\n1. Use Alt,H,G,A,T to align all BLUE rectangles to the TOP\n2. Use Alt,H,G,A,H to distribute them HORIZONTALLY\n3. Use Alt,H,G,A,C to align all GREEN squares to the CENTER\n4. Use Alt,H,G,A,V to distribute them VERTICALLY",
        shapes: [
          // Blue rectangles in random positions
          { x: 250, y: 150, w: 80, h: 50, color: 0x0000ff },
          { x: 350, y: 250, w: 80, h: 50, color: 0x0000ff },
          { x: 450, y: 180, w: 80, h: 50, color: 0x0000ff },
          { x: 550, y: 320, w: 80, h: 50, color: 0x0000ff },
          
          // Green squares of different sizes
          { x: 300, y: 300, w: 60, h: 60, color: 0x00ff00 },
          { x: 400, y: 400, w: 60, h: 60, color: 0x00ff00 },
          { x: 500, y: 500, w: 60, h: 60, color: 0x00ff00 }
        ],
        goal: (scene) => {
          // Log the entire analyticsData for debugging
          console.log("Analytics data:", scene.analyticsData);
          
          // Separate shapes into blue rectangles and green squares
          const blueRectangles = scene.allShapes.filter(s => s.fillColor === 0x0000ff);
          const greenSquares = scene.allShapes.filter(s => s.fillColor === 0x00ff00);
          
          console.log(`Found ${blueRectangles.length} blue rectangles and ${greenSquares.length} green squares`);
          
          // PART 1: Check if blue rectangles are aligned to the top
          const blueTopY = Math.min(...blueRectangles.map(s => s.y - s.displayHeight/2));
          const blueTopAligned = blueRectangles.every(s => 
            Math.abs((s.y - s.displayHeight/2) - blueTopY) < 30
          );
          
          console.log("Blue rectangles top aligned:", blueTopAligned);
          
          // PART 2: Check if blue rectangles are distributed horizontally
          let blueDistributed = true;
          if (blueRectangles.length > 2) {
            const sortedBlue = [...blueRectangles].sort((a, b) => a.x - b.x);
            const leftX = sortedBlue[0].x;
            const rightX = sortedBlue[sortedBlue.length - 1].x;
            const expectedGap = (rightX - leftX) / (sortedBlue.length - 1);
            
            // Check for relatively even spacing with a generous tolerance
            for (let i = 1; i < sortedBlue.length - 1; i++) {
              const expectedX = leftX + (expectedGap * i);
              if (Math.abs(sortedBlue[i].x - expectedX) > Math.max(40, expectedGap * 0.3)) {
                blueDistributed = false;
                break;
              }
            }
          }
          
          console.log("Blue rectangles horizontally distributed:", blueDistributed);
          
          // PART 3: Check if green squares are centered horizontally
          const centerX = scene.slideBackground.x;
          const greenCentered = greenSquares.every(s => 
            Math.abs(s.x - centerX) < 40
          );
          
          console.log("Green squares centered:", greenCentered);
          
          // PART 4: Check if green squares are distributed vertically
          let greenDistributed = true;
          if (greenSquares.length > 2) {
            const sortedGreen = [...greenSquares].sort((a, b) => a.y - b.y);
            const topY = sortedGreen[0].y;
            const bottomY = sortedGreen[sortedGreen.length - 1].y;
            const expectedGap = (bottomY - topY) / (sortedGreen.length - 1);
            
            // Check for relatively even spacing with a generous tolerance
            for (let i = 1; i < sortedGreen.length - 1; i++) {
              const expectedY = topY + (expectedGap * i);
              if (Math.abs(sortedGreen[i].y - expectedY) > Math.max(40, expectedGap * 0.3)) {
                greenDistributed = false;
                break;
              }
            }
          }
          
          console.log("Green squares vertically distributed:", greenDistributed);
          
          // Level is complete if all criteria are met
          const success = blueTopAligned && blueDistributed && greenCentered && greenDistributed;
          console.log("Level completed:", success);
          
          return success;
        },
        timeLimit: 60
      },
      
      // LEVEL 10
      {
        id: 10,
        name: "Professional Layout",
        instructions: "Level 10: Create a professional slide layout:\n1. Use Alt,H,G,A,T to align RED objects to the TOP (these are your title elements)\n2. Use Alt,H,G,A,B to align BLUE objects to the BOTTOM (footer elements)\n3. Use Ctrl+Shift+] to bring the YELLOW shapes to the FRONT\n4. Use Alt,H,G,A,M to align GREEN objects to the MIDDLE vertically\n5. Use Alt,H,G,A,H to distribute GREEN objects HORIZONTALLY",
        shapes: [
          // Title elements (RED)
          { x: 250, y: 150, w: 120, h: 40, color: 0xff0000 },
          { x: 480, y: 180, w: 100, h: 40, color: 0xff0000 },
          
          // Content elements (GREEN)
          { x: 180, y: 280, w: 80, h: 80, color: 0x00ff00 },
          { x: 350, y: 350, w: 80, h: 80, color: 0x00ff00 },
          { x: 520, y: 320, w: 80, h: 80, color: 0x00ff00 },
          
          // Decorative elements (YELLOW) - intended to be in front
          { x: 200, y: 200, w: 60, h: 60, color: 0xffff00 },
          { x: 500, y: 250, w: 60, h: 60, color: 0xffff00 },
          
          // Footer elements (BLUE)
          { x: 300, y: 450, w: 90, h: 30, color: 0x0000ff },
          { x: 450, y: 500, w: 90, h: 30, color: 0x0000ff }
        ],
        goal: (scene) => {
          // Separate shapes by color
          const redShapes = scene.allShapes.filter(s => s.fillColor === 0xff0000);
          const greenShapes = scene.allShapes.filter(s => s.fillColor === 0x00ff00);
          const yellowShapes = scene.allShapes.filter(s => s.fillColor === 0xffff00);
          const blueShapes = scene.allShapes.filter(s => s.fillColor === 0x0000ff);
          
          console.log(`Found: ${redShapes.length} red, ${greenShapes.length} green, ${yellowShapes.length} yellow, ${blueShapes.length} blue shapes`);
          
          // PART 1: Check if red shapes are roughly aligned to the top (very generous tolerance)
          const topQuarter = scene.slideBackground.y - scene.slideBackground.height * 0.25;
          const redTopAligned = redShapes.every(s => s.y < topQuarter);
          console.log("Red shapes top aligned:", redTopAligned);
          
          // PART 2: Check if blue shapes are roughly aligned to the bottom (very generous tolerance)
          const bottomQuarter = scene.slideBackground.y + scene.slideBackground.height * 0.25;
          const blueBottomAligned = blueShapes.every(s => s.y > bottomQuarter);
          console.log("Blue shapes bottom aligned:", blueBottomAligned);
          
          // PART 3: Simple check for yellow shapes being in front - just compare average depths
          const yellowAvgDepth = yellowShapes.reduce((sum, s) => sum + s.depth, 0) / yellowShapes.length;
          const otherAvgDepth = scene.allShapes
            .filter(s => s.fillColor !== 0xffff00)
            .reduce((sum, s) => sum + s.depth, 0) / (scene.allShapes.length - yellowShapes.length);
          const yellowInFront = yellowAvgDepth > otherAvgDepth;
          console.log("Yellow shapes in front:", yellowInFront);
          
          // PART 4: Check if green shapes are roughly in the middle vertically (very generous tolerance)
          const middleRange = scene.slideBackground.height * 0.4; // 40% of slide height
          const slideMiddleY = scene.slideBackground.y;
          const greenMiddleAligned = greenShapes.every(s => 
            Math.abs(s.y - slideMiddleY) < middleRange
          );
          console.log("Green shapes middle aligned:", greenMiddleAligned);
          
          // PART 5: Check if green shapes are somewhat horizontally distributed
          let greenDistributed = true;
          if (greenShapes.length > 2) {
            const sortedGreen = [...greenShapes].sort((a, b) => a.x - b.x);
            // Simple check: leftmost should be in left third, rightmost in right third
            const leftBound = scene.slideBackground.x - scene.slideBackground.width * 0.25;
            const rightBound = scene.slideBackground.x + scene.slideBackground.width * 0.25;
            greenDistributed = sortedGreen[0].x < leftBound && 
                               sortedGreen[sortedGreen.length-1].x > rightBound;
          }
          console.log("Green shapes horizontally distributed:", greenDistributed);
          
          // Verify all required shortcuts were used
          const shortcutsUsed = scene.analyticsData && scene.analyticsData.shortcutsUsed;
          const topAlignUsed = shortcutsUsed && shortcutsUsed["alt,h,g,a,t"] > 0;
          const bottomAlignUsed = shortcutsUsed && shortcutsUsed["alt,h,g,a,b"] > 0;
          const bringToFrontUsed = shortcutsUsed && shortcutsUsed["ctrl,shift,]"] > 0;
          const middleAlignUsed = shortcutsUsed && shortcutsUsed["alt,h,g,a,m"] > 0;
          const horizontalDistributeUsed = shortcutsUsed && shortcutsUsed["alt,h,g,a,h"] > 0;
          
          console.log("Shortcuts used - Top align:", topAlignUsed, "Bottom align:", bottomAlignUsed, 
                      "Bring to front:", bringToFrontUsed, "Middle align:", middleAlignUsed,
                      "Horizontal distribute:", horizontalDistributeUsed);
          
          // Level is complete if both visual criteria and shortcuts are satisfied
          const visualSuccess = redTopAligned && blueBottomAligned && yellowInFront && 
                               greenMiddleAligned && greenDistributed;
          const shortcutSuccess = topAlignUsed && bottomAlignUsed && bringToFrontUsed && 
                                 middleAlignUsed && horizontalDistributeUsed;
          
          console.log("Visual criteria met:", visualSuccess);
          console.log("Shortcut criteria met:", shortcutSuccess);
          
          // Can use either one of these approaches:
          // 1. Require both visual and shortcut criteria (stricter)
          // return visualSuccess && shortcutSuccess;
          
          // 2. Require only that either criteria is met (more lenient)
          return visualSuccess || shortcutSuccess;
        },
        timeLimit: 60
      }
    ];
  }

  // Start a specific level
  startLevel(levelIndex) {
    try {
      if (this.scene.isTransitioning) return;
      
      console.log(`🚀 Starting level ${levelIndex}`);
      
      // Get level data - ALWAYS find by ID, not array index (Bain levels have different indices)
      let levelData = this.scene.levels.find(level => level.id === levelIndex);
      if (!levelData) {
        // For backwards compatibility, try array index for levels 0-10 only
        if (levelIndex >= 0 && levelIndex <= 10) {
        levelData = this.scene.levels[levelIndex];
        }
      }
      
      if (!levelData) {
        console.error('No level data found for level:', levelIndex);
        console.error('Available levels:', this.scene.levels.map(l => ({ id: l.id, name: l.name })));
        // Force 60 second fallback for Bain levels even if level data missing
        if ([22, 23, 24, 25, 28, 29].includes(levelIndex)) {
          this.scene.timeLimit = 60;
          console.log('🔧 Bain level fallback: forcing 60 seconds for missing level', levelIndex);
        }
        return;
      }
      
      // Set current level data
      this.scene.currentLevel = levelIndex;
      this.scene.levelComplete = false;
      // Ensure Bain levels always get 60 seconds, fallback to 60 for any level
      this.scene.timeLimit = [22, 23, 24, 25, 28, 29].includes(levelIndex) ? 60 : (levelData.timeLimit || 60);
      console.log(`🔧 Timer set for level ${levelIndex}: ${this.scene.timeLimit} seconds`);
      
      // Clear existing shapes
      this.scene.clearAllShapes();
      
      const startTime = Date.now();
      console.log('🚀 [TIMING] Level sequence starting at', startTime);
      
      // Clear any existing UI components before showing new ones (but keep the sidebar)
      if (this.scene.rexUIManager) {
        console.log('🧹 [TIMING] Clearing existing UI at', Date.now());
        // Only clear instructions, timer, and notifications - keep the sidebar
        this.scene.rexUIManager.clearInstructionsPanel();
        this.scene.rexUIManager.clearTimerPanel();
        this.scene.rexUIManager.clearGameFlowPanel();
      }
      
      // PHASE 1: Display instructions (immediate) - will auto-hide after 5 seconds
      console.log('📋 [TIMING] PHASE 1: Creating instructions at', Date.now());
      this.scene.rexUIManager.createInstructionsPanel(levelData.instructions, levelData.name);
      
      // PHASE 2: Create shapes (after 1 second - let instructions be seen)
      this.scene.time.delayedCall(1000, () => {
        console.log('📋 [TIMING] PHASE 2: Creating shapes at', Date.now(), '(+1000ms)');
        // Use LevelManager's own createLevelShapes method to ensure proper level data lookup
        this.createLevelShapes();
        
        // PHASE 3: Start countdown immediately for Bain levels, or after 2 seconds for others
        const isBainLevel = levelIndex >= 22 && levelIndex <= 29;
        const countdownDelay = isBainLevel ? 0 : 2000; // Immediate for Bain, 2s for others
        
        this.scene.time.delayedCall(countdownDelay, () => {
          console.log('📋 [TIMING] PHASE 3: Starting countdown at', Date.now(), `(+${1000 + countdownDelay}ms total, Bain: ${isBainLevel})`);
          
          // Initialize timer BEFORE countdown starts - use the scene's timeLimit which was already set correctly
          this.scene.timeRemaining = this.scene.timeLimit; // Use scene.timeLimit which already has our Bain safeguards
          console.log('🔧 Timer initialized before countdown:', this.scene.timeRemaining, 'seconds');
          
          // Create timer panel using the validated scene timeLimit, not levelData
          this.scene.rexUIManager.createTimerPanel(this.scene.timeLimit);
          this.scene.startCountdown();
        });
      });
      
      console.log(`📋 Level ${levelIndex} sequence initiated`);
      
    } catch (error) {
      console.error('Error starting level:', error);
    }
  }

  // Begin level with countdown
  beginLevel(levelIndex) {
    if (this.scene.isTransitioning) return;
    
    try {
      console.log(`Beginning level ${levelIndex} with countdown`);
      
      // Set current level
      this.scene.currentLevel = levelIndex;
      this.scene.levelComplete = false;
      
      // Clear existing shapes
      this.scene.clearAllShapes();
      
      // Create countdown display
      this.scene.createCountdownDisplay();
      
      // Update UI
      this.scene.updateTopBarInfo();
      
    } catch (error) {
      console.error('Error beginning level:', error);
    }
  }

  // Create shapes for current level
  createLevelShapes() {
    console.log('✅ LevelManager.createLevelShapes() called - this is the correct method');
    
    try {
      // Clear existing shapes
      if (this.scene.levelShapes) {
        this.scene.levelShapes.forEach(shape => shape.destroy());
      }
      if (this.scene.allShapes) {
        this.scene.allShapes.forEach(shape => shape.destroy());
      }
      
      // Initialize arrays
      this.scene.levelShapes = [];
      this.scene.allShapes = [];
      this.scene.selectedShapes = [];
      
      // Get current level data - ALWAYS find by ID, not array index (same fix as startLevel)  
      let levelData = this.scene.levels.find(level => level.id === this.scene.currentLevel);
      if (!levelData) {
        // For backwards compatibility, try array index for levels 0-10 only
        if (this.scene.currentLevel >= 0 && this.scene.currentLevel <= 10) {
          levelData = this.scene.levels[this.scene.currentLevel];
        }
      }
      
      if (!levelData || !levelData.shapes) {
        console.error('No shapes data for level:', this.scene.currentLevel);
        console.error('Available levels:', this.scene.levels.map(l => ({ id: l.id, name: l.name })));
        return;
      }
      
      console.log('📊 LevelManager creating shapes for level:', this.scene.currentLevel, levelData.shapes);
      
      // Use the scene's slideArea if available (matches ShapeManager logic)
      let slideLeft, slideTop;
      if (this.scene.slideArea) {
        console.log('📊 Using scene slideArea for level shapes:', this.scene.slideArea);
        slideLeft = this.scene.slideArea.left;
        slideTop = this.scene.slideArea.top;
      } else {
        // Fallback calculation matching the scene's slide positioning
        const sidebarWidth = this.scene.rexUIManager?.sidebarWidth || 250;
        const sidebarGap = this.scene.rexUIManager?.sidebarGap || 20;
        const topBarHeight = 60;
        const bottomBarHeight = 60;
        
        const availableWidth = this.scene.cameras.main.width - sidebarWidth - sidebarGap;
        const availableHeight = this.scene.cameras.main.height - topBarHeight - bottomBarHeight;
        const slideWidth = Math.min(availableWidth * 0.85, 800);
        const slideHeight = Math.min(availableHeight * 0.85, 600);
        
        const slideX = sidebarWidth + sidebarGap + (availableWidth / 2);
        const slideY = topBarHeight + (availableHeight / 2) - 20;
        
        slideLeft = slideX - slideWidth / 2;
        slideTop = slideY - slideHeight / 2;
        
        console.log('📊 Calculated slide positioning:', { slideLeft, slideTop, slideWidth, slideHeight });
      }
      
      // Create shapes from level data, positioned within the slide area
      console.log('📊 Creating shapes for level:', this.scene.currentLevel, 'Shape configs:', levelData.shapes);
      
      levelData.shapes.forEach((shapeConfig, index) => {
        console.log(`📊 Creating shape ${index}:`, shapeConfig);
        
        const shape = this.scene.createShape(
          slideLeft + shapeConfig.x, 
          slideTop + shapeConfig.y, 
          shapeConfig.w, 
          shapeConfig.h, 
          shapeConfig.color,
          false, // isFixed
          false, // isDecoration
          shapeConfig.type || 'rectangle' // shape type for proper creation
        );
        
        // Tag shape with its type for level completion detection
        if (shape) {
          shape.setData('shapeType', shapeConfig.type || 'rectangle');
          console.log(`📊 Shape ${index} created:`, { type: shapeConfig.type, x: shape.x, y: shape.y, width: shape.width, height: shape.height });
        }
        
        // Add to arrays
        this.scene.levelShapes.push(shape);
        this.scene.allShapes.push(shape);
      });
      
      console.log('Created', this.scene.allShapes.length, 'shapes');
      
    } catch (error) {
      console.error('Error creating level shapes:', error);
    }
  }

  // Show level instructions
  showLevelInstructions() {
    try {
      const levelData = this.scene.levels[this.scene.currentLevel];
      if (!levelData || !levelData.instructions) return;
      
      // Create instructions overlay
      this.scene.instructionsOverlay = this.scene.add.rectangle(
        this.scene.cameras.main.width / 2,
        this.scene.cameras.main.height / 2,
        this.scene.cameras.main.width * 0.8,
        this.scene.cameras.main.height * 0.6,
        0x000000,
        0.8
      );
      
      this.scene.instructionsOverlay.setDepth(1000);
      
      // Create instructions text
      this.scene.instructionsText = this.scene.add.text(
        this.scene.cameras.main.width / 2,
        this.scene.cameras.main.height / 2,
        levelData.instructions,
        {
          fontSize: '24px',
          fill: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          wordWrap: { width: this.scene.cameras.main.width * 0.7 }
        }
      ).setOrigin(0.5);
      
      this.scene.instructionsText.setDepth(1001);
      
      // Fade out after 3 seconds
      this.scene.time.delayedCall(3000, () => {
        if (this.scene.instructionsOverlay) {
          this.scene.instructionsOverlay.destroy();
          this.scene.instructionsOverlay = null;
        }
        if (this.scene.instructionsText) {
          this.scene.instructionsText.destroy();
          this.scene.instructionsText = null;
        }
      });
      
    } catch (error) {
      console.error('Error showing level instructions:', error);
    }
  }
}